import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import LandingNav from '../components/LandingNav';
import './Landing.css';

// ============================================================
// Copy (bilingual — first draft, expected to be rewritten)
// ============================================================
const COPY = {
  en: {
    nav: {
      features: 'Features',
      philosophy: 'Why',
      about: 'About',
      blog: 'Blog',
      reviews: 'Reviews',
      faq: 'FAQ',
      contact: 'Contact',
      login: 'Log in',
      signup: 'Start free',
    },
    hero: {
      tagline: 'Teach smarter,',
      taglineEm: 'not harder',
      sub: 'For teachers who got into this to teach.',
      cta: 'Start free',
      searchPlaceholder: 'What will you teach today?',
      suggestionsLabel: 'Try one of these',
      suggestions: [
        'Past simple for absolute beginners',
        'IELTS speaking practice, intermediate level',
        'Business English: meetings & negotiations',
        'Present perfect vs past simple, with examples',
        'Travel & tourism vocabulary for teenagers',
      ],
    },
    // `|...|` → brand-gradient phrase. `<<...>>` → brand wordmark
    // (NextSphere font + decorative dot inside any 'O').
    manifesto: [
      'You became a teacher\nto |change lives|',
      'not to fight\nwith eight tabs',
      '<<NOVAS>> isn’t a product.\nIt’s a |new philosophy| of teaching',
    ],
    stats: [
      { value: '30s', label: 'A full lesson, generated' },
      { value: '20+', label: 'Exercise types, every level' },
      { value: '1', label: 'Place for your entire business' },
    ],
    diagramTitle: 'From eight tabs\nto |one workspace|',
    showcase: {
      eyebrow: 'See it work',
      title: 'A workspace,\nnot a |toolbar|',
      sub: 'Three views, one product. The same lesson follows your student from your editor to their dashboard to your video call.',
      shots: [
        {
          key: 'editor',
          label: 'Lesson editor',
          caption: 'Drop a link, a topic, or an image. AI returns a full lesson — reading, vocabulary, and exercises — leveled to the student, in under a minute.',
          url: 'novas.app/lessons/today',
          // Drop the file into public/landing/ and set the path here.
          // Renderer prefers `video`, then `image`, then a placeholder.
          video: '/landing/LessonWorkspace.mp4',
        },
        {
          key: 'call',
          label: 'The session',
          caption: 'Whiteboard, lesson, and HD video in the same tab. Teacher and student see the same exercise update in real time — nothing to install.',
          url: 'novas.app/call',
          video: '/landing/videocall.mp4',
        },
        {
          key: 'student',
          label: 'Student dashboard',
          caption: 'Their lessons, homework, deadlines, and dictionary — under one login. No app to install.',
          url: 'novas.app/anna',
          video: '/landing/StudentProfile.mp4',
        },
      ],
    },
    features: [
      {
        key: 'LESSONS',
        title: 'Lessons',
        text: 'AI builds a full lesson from any link, topic, or image — in thirty seconds.',
      },
      {
        key: 'EXERCISES',
        title: 'Exercises',
        text: '20+ exercise types: fill-in-blank, matching, drag-and-drop, transcription, and more. Generated for the level you teach.',
      },
      {
        key: 'AUTHENTIC',
        title: 'Authentic',
        text: 'BBC articles. YouTube clips. Reddit threads. Real material — turned into a real lesson.',
      },
      {
        key: 'HOMEWORK',
        title: 'Homework',
        text: 'Deadlines that follow themselves up. Telegram reminders. Progress you can actually see.',
      },
      {
        key: 'VIDEO',
        title: 'Video calls',
        text: 'Built-in HD video right inside the lesson. No Zoom, no install, no separate link — your student joins from the same page they study on.',
      },
      {
        key: 'CHAT',
        title: 'Chat',
        text: 'Conversations with your students that don’t get lost in WhatsApp.',
      },
      {
        key: 'BUSINESS',
        title: 'Business',
        text: 'Schedule, library, video calls, profiles, billing. One place. Yours.',
      },
    ],
    closing: {
      line: 'Become the |teacher|\n|you wanted| to be',
      cta: 'Start free',
    },
    about: {
      eyebrow: 'About us',
      title: 'Built by people who got tired of waiting for it',
      intro: 'NOVAS started because we couldn’t find the tool we needed — so we built it ourselves.',
      story: 'We’ve sat on both sides of the screen — the teacher’s and the student’s. We watched brilliant educators lose half their evening copy-pasting between Google Docs, Quizlet, Telegram and Notion. We started NOVAS because we believe a teacher’s hour should go into teaching — not into juggling eight browser tabs. One workspace, built around the lesson instead of against it.',
      foundersHeading: 'The founders',
      founders: [
        {
          name: 'Samvel Srmikian',
          role: 'CEO & Founder',
          bio: 'Started as an English lecturer, building courses that reached 200+ students. Went on to run international partnerships for a public university — a $500k program, 113% growth, a team of 30 — before leading content operations for a U.S. creative agency. Builds NOVAS from the classroom out.',
          linkedin: 'https://www.linkedin.com/in/samvel-srmikian-582619174/',
          photo: '/landing/founder-samvel.jpg',
        },
        {
          name: 'Kristina Salina',
          role: 'CPO & Co-Founder',
          bio: 'Product manager shaped by Kommo, Estée Lauder, and three years as Head of Customer Care at LinguaTrip — the EdTech company where she first saw what teachers and students actually need. Obsessive about metrics, onboarding, and the details a teacher notices at 11pm.',
          linkedin: 'https://www.linkedin.com/in/kristina-salina/?skipRedirect=true',
          photo: '/landing/founder-kristina.jpg',
        },
      ],
    },
    footer: {
      rights: '© 2026 NOVAS',
      privacy: 'Privacy',
      terms: 'Terms',
      contact: 'Contact',
    },
  },
  ru: {
    nav: {
      features: 'Возможности',
      philosophy: 'Зачем',
      about: 'О нас',
      blog: 'Блог',
      reviews: 'Отзывы',
      faq: 'FAQ',
      contact: 'Контакты',
      login: 'Войти',
      signup: 'Попробовать',
    },
    hero: {
      tagline: 'Teach smarter,',
      taglineEm: 'not harder',
      sub: 'Для тех, кто пришёл в профессию учить.',
      cta: 'Попробовать',
      searchPlaceholder: 'Что будете преподавать сегодня?',
      suggestionsLabel: 'Попробуйте один из вариантов',
      suggestions: [
        'Past Simple для начинающих',
        'Разговорная практика IELTS, средний уровень',
        'Деловой английский: встречи и переговоры',
        'Present Perfect vs Past Simple, с примерами',
        'Лексика по теме "путешествия" для подростков',
      ],
    },
    // `|...|` → brand-gradient phrase. `<<...>>` → brand wordmark
    // (NextSphere font + decorative dot inside any 'O').
    manifesto: [
      'Ты пошёл в эту профессию,\nчтобы |менять жизни|',
      'А не воевать\nс восемью вкладками',
      '<<NOVAS>> — это не продукт.\nЭто |новая философия| преподавания',
    ],
    stats: [
      { value: '30 сек', label: 'Полный урок генерируется' },
      { value: '20+', label: 'Типов заданий, любой уровень' },
      { value: '1', label: 'Место для всего твоего бизнеса' },
    ],
    diagramTitle: 'От восьми вкладок\nк |одному рабочему месту|',
    showcase: {
      eyebrow: 'Как это выглядит',
      title: 'Рабочее место,\nа не |панель инструментов|',
      sub: 'Три экрана, один продукт. Урок едет за студентом — из твоего редактора в его дашборд и в твой видеозвонок.',
      shots: [
        {
          key: 'editor',
          label: 'Редактор урока',
          caption: 'Вставляешь ссылку, тему или картинку — AI собирает полный урок: чтение, словарь и упражнения, подобранные под уровень студента, меньше чем за минуту.',
          url: 'novas.app/lessons/today',
          video: '/landing/LessonWorkspace.mp4',
        },
        {
          key: 'call',
          label: 'Занятие',
          caption: 'Доска, урок и HD-видео в одной вкладке. Преподаватель и студент видят одно и то же упражнение в реальном времени — ничего не нужно устанавливать.',
          url: 'novas.app/call',
          video: '/landing/videocall.mp4',
        },
        {
          key: 'student',
          label: 'Дашборд студента',
          caption: 'Его уроки, домашка, дедлайны и словарь — под одним логином. Никаких приложений ставить не надо.',
          url: 'novas.app/anna',
          video: '/landing/StudentProfile.mp4',
        },
      ],
    },
    features: [
      {
        key: 'LESSONS',
        title: 'Уроки',
        text: 'AI собирает полный урок из любой ссылки, темы или картинки — за тридцать секунд.',
      },
      {
        key: 'EXERCISES',
        title: 'Задания',
        text: '20+ типов: fill-in-blank, matching, drag-and-drop, транскрипция и другие. Под уровень твоего студента.',
      },
      {
        key: 'AUTHENTIC',
        title: 'Материалы',
        text: 'Статьи BBC. Ролики YouTube. Посты Reddit. Реальный контент — превращённый в реальный урок.',
      },
      {
        key: 'HOMEWORK',
        title: 'Домашка',
        text: 'Дедлайны, которые сами себя напоминают. Уведомления в Telegram. Прогресс, который видно.',
      },
      {
        key: 'VIDEO',
        title: 'Видеосвязь',
        text: 'HD-видеосвязь прямо внутри урока. Без Zoom, без установки, без отдельной ссылки — студент заходит на ту же страницу, где идёт занятие.',
      },
      {
        key: 'CHAT',
        title: 'Чат',
        text: 'Разговоры со студентами, которые не теряются в WhatsApp.',
      },
      {
        key: 'BUSINESS',
        title: 'Бизнес',
        text: 'Расписание, библиотека, профили, оплаты. В одном месте. Твоё.',
      },
    ],
    closing: {
      line: 'Стать |учителем|\n|нового времени|',
      cta: 'Попробовать',
    },
    about: {
      eyebrow: 'О нас',
      title: 'Сделано теми, кому надоело ждать, пока это сделает кто-то другой',
      intro: 'NOVAS появился потому, что мы не могли найти инструмент, который был нам нужен. И мы его сделали сами.',
      story: 'Мы сидели по обе стороны экрана — и за преподавателем, и за студентом. Мы видели, как талантливые учителя теряют полвечера на копипаст между Google Docs, Quizlet, Telegram и Notion. NOVAS родился из убеждения: час преподавателя должен уходить в обучение, а не в жонглирование вкладками. Одно рабочее место, выстроенное вокруг урока, а не наоборот.',
      foundersHeading: 'Основатели',
      founders: [
        {
          name: 'Samvel Srmikian',
          role: 'CEO & Founder',
          bio: 'Начинал как преподаватель английского — курсы охватили 200+ студентов. Затем руководил международными партнёрствами в государственном университете: программа на $500k, рост 113%, команда из 30 человек — а после возглавлял контент-операции в американском креативном агентстве. Строит NOVAS от класса, а не от офиса.',
          linkedin: 'https://www.linkedin.com/in/samvel-srmikian-582619174/',
          photo: '/landing/founder-samvel.jpg',
        },
        {
          name: 'Kristina Salina',
          role: 'CPO & Co-Founder',
          bio: 'Продакт-менеджер, прошедшая школу Kommo, Estée Lauder и три года во главе Customer Care в LinguaTrip — EdTech-компании, где она впервые увидела, что на самом деле нужно учителям и студентам. Одержима метриками, онбордингом и деталями, которые преподаватель замечает в 11 вечера.',
          linkedin: 'https://www.linkedin.com/in/kristina-salina/?skipRedirect=true',
          photo: '/landing/founder-kristina.jpg',
        },
      ],
    },
    footer: {
      rights: '© 2026 NOVAS',
      privacy: 'Приватность',
      terms: 'Условия',
      contact: 'Контакты',
    },
  },
};

// ============================================================
// Hooks
// ============================================================
function useInView(threshold = 0.25, once = true, edgeCheck = false) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let obs = null;
    let raf2 = null;
    // Landing's own scroll effect (hero/slogan/bounce shrink-to-dot
    // chain) switches several full-screen sections from normal flow to
    // position: fixed on mount, which shrinks the total page height by
    // hundreds of vh in one step. Starting the observer immediately
    // could catch that transient pre-fix layout — sections far below
    // the fold were showing up already marked is-visible (confirmed via
    // DevTools) despite never having actually scrolled into view. Two
    // rAFs pushes the observer's start past both that layout flip and
    // its subsequent paint.
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        obs = new IntersectionObserver(
          ([entry]) => {
            // edgeCheck: trigger by the element's actual top edge
            // reaching the viewport top (i.e. it now fully covers the
            // screen) instead of by intersection ratio — ratio depends
            // on the element's own height, which for a block taller
            // than the viewport made a fixed ratio unreliable (it was
            // either crossed too early, while the previous block was
            // still partly visible, or — pushed higher to compensate —
            // never reachable at all depending on the exact viewport).
            // The edge position is a fixed, unambiguous geometric fact.
            const passed = edgeCheck
              ? entry.boundingClientRect.top <= 0
              : entry.isIntersecting;
            if (passed) {
              setInView(true);
              if (once) obs.disconnect();
            } else if (!once) {
              // Only ever reached when once = false — resets so the
              // reveal transition can play again the next time this
              // scrolls back into view, instead of staying "seen"
              // forever after the first pass.
              setInView(false);
            }
          },
          edgeCheck
            // Many steps so the callback re-fires often enough to catch
            // the top-edge crossing promptly during a normal scroll.
            ? { threshold: Array.from({ length: 21 }, (_, i) => i / 20) }
            : { threshold },
        );
        obs.observe(el);
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2 !== null) cancelAnimationFrame(raf2);
      if (obs) obs.disconnect();
    };
  }, [threshold, once, edgeCheck]);
  return [ref, inView];
}

// Tween a number from 0 to target over duration ms once in-view.
function useCountUp(target, inView, duration = 1400) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!inView) return;
    // Pull the digits out of strings like "30 сек" or "6+" so we can animate
    // them; the unit/suffix is appended back in the JSX.
    const match = String(target).match(/^(\d+)/);
    if (!match) {
      setValue(target);
      return;
    }
    const end = parseInt(match[1], 10);
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      setValue(Math.round(end * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, inView, duration]);
  return value;
}

// ============================================================
// Main component
// ============================================================
export default function Landing() {
  const navigate = useNavigate();

  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem('novas-landing-lang') || 'en'; }
    catch (_) { return 'en'; }
  });

  const t = COPY[lang];

  // Hero search suggestions — purely a UI hint for now (like a normal
  // search engine's autocomplete), no generation wired up yet. Filters
  // the static example-prompt list as the teacher types; falls back to
  // showing all of them when the field is empty or nothing matches, so
  // the box is never just blank while focused.
  const [searchValue, setSearchValue] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const searchSuggestions = (() => {
    const q = searchValue.trim().toLowerCase();
    if (!q) return t.hero.suggestions;
    const filtered = t.hero.suggestions.filter((s) => s.toLowerCase().includes(q));
    return filtered.length ? filtered : t.hero.suggestions;
  })();

  useEffect(() => {
    // Force dark on the landing — we'll keep the data-attribute namespaced
    // to landing-only so the rest of the app keeps its own styling.
    document.documentElement.setAttribute('data-landing-theme', 'dark');
    return () => document.documentElement.removeAttribute('data-landing-theme');
  }, []);

  useEffect(() => {
    // Chrome restores the previous scroll position on a plain reload by
    // default — after testing the shrink-to-dot effect further down the
    // page, a reload was landing back inside .landing-slogan instead of
    // at the hero. Opting out of that restoration (and resetting to the
    // top, unless the URL points at a specific in-page anchor) keeps a
    // fresh load always starting at the actual top of the page.
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    if (!window.location.hash) {
      window.scrollTo(0, 0);
    }
  }, []);

  useEffect(() => {
    try { localStorage.setItem('novas-landing-lang', lang); } catch (_) {}
  }, [lang]);

  const toggleLang = () => setLang((prev) => (prev === 'en' ? 'ru' : 'en'));

  // Scroll-driven "shrink to a dot" transition: block 1 (the hero video)
  // collapses into a small black dot as the user scrolls one viewport's
  // worth, and that dot lands exactly where a permanent, ordinary
  // .landing-slogan-dot element already sits in the page — so the
  // moment the animation finishes, hero is hidden and the static dot
  // silently takes over, with no visible handoff. First pass, hero→
  // slogan only, per request — later blocks aren't wired up yet.
  const heroRef = useRef(null);
  const heroInnerRef = useRef(null);
  const heroSpacerRef = useRef(null);
  const slogRef = useRef(null);
  const slogInnerRef = useRef(null);
  const slogSpacerRef = useRef(null);
  // Landed dots live as top-level siblings of every section (rendered
  // near the end of this component, always in the DOM, positioned/shown
  // via direct ref mutation) rather than inside the section that
  // produced them. Reason: block 2 (slogan) is itself the shrinking
  // source for the NEXT transition, and if block 1's landed dot were
  // still a child of slogan, slogan's own shrink transform would drag
  // that dot along with it instead of leaving it behind permanently.
  const dot1Ref = useRef(null);
  const dot2Ref = useRef(null);
  const dot3Ref = useRef(null);
  // Transition 3: the "You became a teacher / to change lives" phrase
  // drifts left as dot2 itself — the same ball that landed above this
  // section at the end of transition 2 — falls onto the first word and
  // hops word by word across it, then "Not to fight with eight tabs"
  // slides in from the right and the ball keeps hopping across ITS
  // words too. Reusing dot2 (rather than fading it out and fading in a
  // separate ball) matters: two different elements swapping read as a
  // new ball appearing, not the same one continuing.
  const bounceRef = useRef(null);
  const bouncePhrase1Ref = useRef(null);
  const bouncePhrase2Ref = useRef(null);
  const bounceSpacerRef = useRef(null);
  // Transition 4: #philosophy itself is the shrink-to-dot SOURCE here,
  // same mechanism as hero (transition 1) and slogan (transition 2) —
  // pin, scale + border-radius toward a target point, land a permanent
  // dot. dot4 is the landed marker (page-level sibling, same reasoning
  // as dot1-3: a later section's own shrink transform must never drag
  // an earlier landed dot along with it).
  const philoRef = useRef(null);
  const philoInnerRef = useRef(null);
  const philoSpacerRef = useRef(null);
  const dot4Ref = useRef(null);
  useEffect(() => {
    const hero = heroRef.current;
    const heroInner = heroInnerRef.current;
    const slog = slogRef.current;
    const slogInner = slogInnerRef.current;
    const dot1 = dot1Ref.current;
    const dot2 = dot2Ref.current;
    const dot3 = dot3Ref.current;
    const bounce = bounceRef.current;
    const bouncePhrase1 = bouncePhrase1Ref.current;
    const bouncePhrase2 = bouncePhrase2Ref.current;
    const philo = philoRef.current;
    const philoInner = philoInnerRef.current;
    const dot4 = dot4Ref.current;
    if (!hero || !heroInner || !slog || !slogInner || !dot1 || !dot2 || !dot3
      || !bounce || !bouncePhrase1 || !bouncePhrase2 || !philo || !philoInner || !dot4) return;
    const DOT = 22;      // final dot diameter, px
    const NAV_GAP = 74;  // clearance below the nav pill's actual bottom edge
    // The bounce block stays one screen tall on-screen, but needs a much
    // longer scroll DISTANCE than that to feel smooth (13 words hopped
    // across just 100vh meant ~60-80px per hop — chaotic flicking, not a
    // bounce). This stretches transition 3's range without touching the
    // block's own visual height.
    const BOUNCE_SCROLL_MULT = 2.5;
    // #philosophy's own transition 4 range is longer than its natural
    // height too — partly held fully visible/readable (see HOLD_FRAC
    // below), partly spent on the shrink itself.
    const PHILO_SCROLL_MULT = 1.4;

    // Natural (in-flow, pre-fixed) sizes, captured once up front — each
    // transition's scroll range is measured in these, chained end to end
    // (transition 2 starts exactly where transition 1's reserved space
    // ends). The spacers' own height is set here too (not in CSS) so the
    // page's actual scroll length always matches what the math below
    // assumes, even though .landing-slogan's natural height depends on
    // its content/viewport and isn't a fixed number.
    let heroH = hero.getBoundingClientRect().height;
    let slogH = slog.getBoundingClientRect().height;
    let bounceH = bounce.getBoundingClientRect().height;
    let philoH = philo.getBoundingClientRect().height;
    const remeasure = () => {
      // Skip re-measuring whichever section is currently mid-transition
      // (position: fixed, scaled down) — its rendered box no longer
      // reflects its natural size, so a mid-animation resize would
      // otherwise corrupt heroH/slogH/bounceH/philoH with a shrunk
      // reading.
      if (hero.style.position !== 'fixed') heroH = hero.getBoundingClientRect().height;
      if (slog.style.position !== 'fixed') slogH = slog.getBoundingClientRect().height;
      if (bounce.style.position !== 'fixed') bounceH = bounce.getBoundingClientRect().height;
      if (philo.style.position !== 'fixed') philoH = philo.getBoundingClientRect().height;
      if (heroSpacerRef.current) heroSpacerRef.current.style.height = `${heroH}px`;
      if (slogSpacerRef.current) slogSpacerRef.current.style.height = `${slogH}px`;
      if (bounceSpacerRef.current) bounceSpacerRef.current.style.height = `${bounceH * BOUNCE_SCROLL_MULT}px`;
      if (philoSpacerRef.current) philoSpacerRef.current.style.height = `${philoH * PHILO_SCROLL_MULT}px`;
    };

    // Frozen page-absolute Y for each dot, set once the first time its
    // transition lands and never recomputed after — since it's meant to
    // be a permanent, page-scrolling-normally marker, not something that
    // tracks the live viewport.
    let dot1PageY = null;
    let dot2PageY = null;
    let dot3PageY = null;
    let dot4PageY = null;
    let philoRevealAt = null;
    let raf = null;
    const onScroll = () => {
      if (raf !== null) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        const y = window.scrollY;
        // Measured live, not a hardcoded constant: the nav pill shrinks
        // (.is-shrunk) partway down the page, so its real bottom edge
        // keeps changing. Both dots target the same spot below it, so
        // they land at a consistent height each time.
        const navEl = document.querySelector('.landing-nav');
        const targetY = (navEl ? navEl.getBoundingClientRect().bottom : 76) + NAV_GAP;

        // ---- Transition 1: hero -> dot above the slogan ----
        const H1 = heroH || window.innerHeight;
        const p1 = Math.max(0, Math.min(1, y / H1));
        if (p1 >= 1) {
          hero.style.visibility = 'hidden';
          if (dot1PageY === null) dot1PageY = y + targetY;
          dot1.style.top = `${dot1PageY}px`;
          dot1.style.opacity = '1';
        } else {
          // Hides while transition 1 is actually live/reversing (e.g.
          // scrolling back up through it): hero itself is visibly
          // occupying this exact spot then, so showing the static dot
          // at the same time would double up, same as before landing.
          dot1.style.opacity = '0';
          hero.style.visibility = 'visible';
          hero.style.position = 'fixed';
          hero.style.top = '0';
          hero.style.left = '0';
          const w = window.innerWidth;
          // Zoom straight into the target point instead of scaling
          // around the center and separately translating toward it: a
          // flat px translate is imperceptible while the block is still
          // huge, then suddenly visible once it's small — reading as a
          // jump/snap even though it was mathematically linear the whole
          // time. Pinning transform-origin to the target's own position
          // within the box keeps that exact point fixed on screen from
          // frame one; everything else visibly converges into it.
          hero.style.transformOrigin = `50% ${(targetY / H1) * 100}%`;
          hero.style.transform = `scale(${1 - p1 * (1 - DOT / w)}, ${1 - p1 * (1 - DOT / H1)})`;
          const big1 = Math.max(w, H1);
          const topR1 = p1 * big1;
          const botR1 = 48 + p1 * (big1 - 48);
          hero.style.borderRadius = `${topR1}px ${topR1}px ${botR1}px ${botR1}px`;
          heroInner.style.opacity = String(Math.max(0, 1 - p1 * 1.6));
        }

        // ---- Transition 2: slogan -> dot above the manifesto ----
        // Starts exactly where transition 1's own reserved space (H1)
        // ends, so there's no dead gap and no overlap between the two.
        if (y < H1) {
          slog.style.visibility = 'visible';
          slog.style.position = '';
          slog.style.top = '';
          slog.style.left = '';
          slog.style.transform = '';
          slog.style.transformOrigin = '';
          slog.style.borderRadius = '';
          slogInner.style.opacity = '1';
        } else {
          const H2 = slogH || window.innerHeight;
          const p2 = Math.max(0, Math.min(1, (y - H1) / H2));
          if (p2 >= 1) {
            slog.style.visibility = 'hidden';
            if (dot2PageY === null) dot2PageY = y + targetY;
            dot2.style.top = `${dot2PageY}px`;
            dot2.style.opacity = '1';
          } else {
            dot2.style.opacity = '0';
            slog.style.visibility = 'visible';
            slog.style.position = 'fixed';
            slog.style.top = '0';
            slog.style.left = '0';
            const w2 = window.innerWidth;
            slog.style.transformOrigin = `50% ${(targetY / H2) * 100}%`;
            slog.style.transform = `scale(${1 - p2 * (1 - DOT / w2)}, ${1 - p2 * (1 - DOT / H2)})`;
            const big2 = Math.max(w2, H2);
            const r2 = p2 * big2;
            slog.style.borderRadius = `${r2}px`;
            slogInner.style.opacity = String(Math.max(0, 1 - p2 * 1.6));
          }
        }

        // ---- Transition 3: bounce phrase 1 -> phrase 2 ----
        // Deliberately reuses dot2 itself as the ball here instead of a
        // separate element that fades in as dot2 fades out — the user
        // pointed out that read as a second ball appearing, not the same
        // one continuing. dot2 switches from its normal page-anchored
        // resting spot (position: absolute, set by transition 2 above)
        // to position: fixed for the duration of this transition, so it
        // can be driven by the same viewport-relative word coordinates
        // as everything else here, then switches back once this section
        // releases.
        const H1H2 = H1 + (slogH || window.innerHeight);
        const H3 = (bounceH || window.innerHeight) * BOUNCE_SCROLL_MULT;
        if (y < H1H2) {
          bounce.style.visibility = 'visible';
          bounce.style.position = '';
          bounce.style.top = '';
          bounce.style.left = '';
          bounce.style.background = '';
          // Mirrors the mobile branch's own resting position (its p3 = 0
          // case) below instead of a flat, unshifted center — bounce
          // isn't position: fixed yet here, so on a narrow phone it can
          // already be scrolling into view in normal flow (right after
          // the slogan spacer) before y actually crosses H1H2. Without
          // this, that meant the phrase visibly snapped from "centered,
          // 'You' off-screen" to "shifted, 'You' visible" the instant
          // the active branch below took over — same text, two
          // different resting positions.
          if (window.innerWidth <= 600) {
            const phrase1WidthVw = (bouncePhrase1.getBoundingClientRect().width / window.innerWidth) * 100;
            const restShift = Math.max(0, (phrase1WidthVw - 100) / 2) + 4;
            bouncePhrase1.style.transform = `translate(-50%, -50%) translateX(${restShift}vw)`;
          } else {
            bouncePhrase1.style.transform = 'translate(-50%, -50%)';
          }
          bouncePhrase2.style.transform = 'translate(-50%, -50%)';
          bouncePhrase2.style.opacity = '0';
          for (const w of [...bouncePhrase1.children, ...bouncePhrase2.children]) {
            w.classList.remove('landing-bounce-word--hit');
          }
          dot2.style.position = '';
          dot2.style.left = '';
        } else {
          const p3 = Math.max(0, Math.min(1, (y - H1H2) / H3));
          const words1 = bouncePhrase1.children.length;
          const words2 = bouncePhrase2.children.length;
          const total = words1 + words2;
          const wordAt = (i) => (i < words1 ? bouncePhrase1.children[i] : bouncePhrase2.children[i - words1]);
          bounce.style.visibility = p3 >= 1 ? 'hidden' : 'visible';
          // #philosophy's own reveal (text fade/blur-in) is driven
          // directly here, in lockstep with the exact same p3 >= 1
          // condition that hides block 3 — that's the precise instant
          // the screen actually goes white, which no ratio- or edge-
          // based heuristic on #philosophy's own geometry could line up
          // with as reliably (its manualReveal prop skips its own
          // guess entirely).
          const philoJustRevealed = p3 >= 1 && philoRevealAt === null;
          document.querySelector('#philosophy')?.classList.toggle('is-visible', p3 >= 1);
          if (philoJustRevealed) philoRevealAt = Date.now();
          if (p3 < 1) philoRevealAt = null;
          if (p3 >= 1) {
            dot2.style.opacity = '0';
            // #philosophy's own reveal (is-visible, toggled above) is
            // just its existing fade/blur text reveal, same as every
            // other manifesto heading. Its own pin-and-shrink transition
            // (transition 4, below) starts independently once this
            // block's flow spacer comes into scroll range — dot3 here
            // just flies into the O once, via a plain CSS transition,
            // the first time the heading scrolls close enough to be
            // visible; unrelated to whether the section is fixed yet.
            //
            // Has to wait for the text's OWN reveal transition (1200ms,
            // plus up to 420ms of per-line stagger delay) to fully
            // settle first — the "O" is inside that animating text, so
            // measuring its position too early grabs it mid-flight
            // (still offset by the transition's translateY/blur), not
            // its final resting spot, landing the dot permanently in
            // the wrong place once the text finishes settling.
            const textSettled = philoRevealAt !== null && Date.now() - philoRevealAt >= 1700;
            if (dot3PageY === null && textSettled) {
              const philoRect = document.querySelector('#philosophy')?.getBoundingClientRect();
              if (philoRect && philoRect.top < window.innerHeight * 0.6) {
                const oEl = document.querySelector('#philosophy .landing-brand-o');
                const oRect = oEl ? oEl.getBoundingClientRect() : null;
                if (oRect) {
                  dot3PageY = y + oRect.top + oRect.height / 2;
                  const targetX = oRect.left + oRect.width / 2;
                  const finalSize = getComputedStyle(oEl).fontSize;
                  dot3.style.top = `${dot3PageY}px`;
                  dot3.style.fontSize = finalSize;
                  dot3.style.transition = 'none';
                  dot3.style.left = `${targetX - 140}px`;
                  dot3.style.width = '22px';
                  dot3.style.height = '22px';
                  dot3.style.opacity = '1';
                  requestAnimationFrame(() => {
                    dot3.style.transition = 'left 550ms cubic-bezier(0.22, 1, 0.36, 1), width 550ms ease, height 550ms ease';
                    dot3.style.left = `${targetX}px`;
                    dot3.style.width = '0.13em';
                    dot3.style.height = '0.13em';
                    oEl.classList.add('landing-brand-o--replaced');
                    dot3.classList.add('landing-chain-dot--o-ricochet');
                  });
                }
              }
            }
          } else {
            // Only hide dot3 here if it never landed yet — once it has
            // (dot3PageY set), it's meant to be a permanent marker like
            // dot1/dot2, and should stay visible even after scrolling
            // back up into this section and back down again.
            if (dot3PageY === null) dot3.style.opacity = '0';
            bounce.style.position = 'fixed';
            bounce.style.top = '0';
            bounce.style.left = '0';

            const FALL_FRAC = 0.06; // fraction spent falling onto word 1
            const HOP_END = 0.85;   // hops end here, landing on the last word

            const splitPoint = words1 / total;
            const isMobileBounce = window.innerWidth <= 600;

            if (isMobileBounce) {
              // Below 600px, treat both phrases as ONE continuous line
              // instead of phrase 1 exiting left while phrase 2 enters
              // from the right — that handoff was tuned in 60vw units
              // that don't scale with .landing-bounce-phrase's 28px
              // font-size floor, so on a narrow phone phrase 1 either
              // never fully cleared before phrase 2 slid in underneath
              // it (overlapping text) or, once the exit distance was
              // fixed to scale with the phrase's own width, the jump
              // between "phrase 1's resting shift" and "phrase 2's own
              // resting shift" read as the text snapping to a different
              // position right as the section came into view. Placing
              // phrase 2 immediately after phrase 1 and scrolling the
              // pair together sidesteps both: there's no handoff moment
              // to get wrong.
              const phrase1WidthVw = (bouncePhrase1.getBoundingClientRect().width / window.innerWidth) * 100;
              const phrase2WidthVw = (bouncePhrase2.getBoundingClientRect().width / window.innerWidth) * 100;
              const GAP_VW = 6; // breathing room standing in for the missing "," between phrases
              const combinedWidthVw = phrase1WidthVw + GAP_VW + phrase2WidthVw;
              // Same idea as the old shift1: bring "You" onto screen at
              // rest (plus a small margin) instead of leaving the line
              // centered — centered would put half of it off the left
              // edge on any phrase wider than the viewport.
              const restShift = Math.max(0, (phrase1WidthVw - 100) / 2) + 4;
              // Generous on purpose (the full combined width plus a
              // viewport's worth of margin) so the whole two-phrase
              // strip is guaranteed to have cleared the left edge by
              // p3 = 1 regardless of how long either phrase runs.
              const travelVw = restShift + 100 + combinedWidthVw;
              // Eased (p3²), not linear — keeps "You" in place a little
              // longer before the scroll picks up speed, same reasoning
              // as the old per-phrase easing.
              const combinedShift = restShift - (p3 * p3) * travelVw;
              bouncePhrase1.style.opacity = '1';
              bouncePhrase1.style.transform = `translate(-50%, -50%) translateX(${combinedShift}vw)`;
              bouncePhrase2.style.opacity = '1';
              bouncePhrase2.style.transform = `translate(-50%, -50%) translateX(${combinedShift + phrase1WidthVw / 2 + GAP_VW + phrase2WidthVw / 2}vw)`;
            } else {
              // Phrase 1 drifts left at a visible pace while its own words
              // are still being hopped on (0 to splitPoint), then keeps
              // drifting the rest of the way to fully offscreen (150vw
              // total) by p3 = 1.
              const e1 = Math.max(0, Math.min(1, p3 / splitPoint));
              let p1x = -e1 * 60;
              if (p3 > splitPoint) {
                const extra1 = (p3 - splitPoint) / (1 - splitPoint);
                p1x = -60 - extra1 * 90;
              }
              bouncePhrase1.style.transform = `translate(-50%, -50%) translateX(${p1x}vw)`;

              // Phrase 2 actually enters — slides AND fades in together —
              // over a window that finishes BEFORE splitPoint, so it's
              // already fully in place and opaque by the time the ball
              // starts hopping on it, instead of still sliding/fading in
              // (or worse, an instant opacity toggle) while the ball's
              // already there. It then just sits still through the hop,
              // and only picks up the extra distance needed for full
              // clearance (150vw) during its own exit after HOP_END.
              const preStart2 = Math.max(0, splitPoint - 0.12);
              const entrance2 = Math.max(0, Math.min(1, (p3 - preStart2) / (splitPoint - preStart2)));
              const exit2 = p3 > HOP_END ? (p3 - HOP_END) / (1 - HOP_END) : 0;
              bouncePhrase2.style.transform = `translate(-50%, -50%) translateX(${(1 - entrance2) * 60 - exit2 * 150}vw)`;
              bouncePhrase2.style.opacity = String(entrance2);
            }

            dot2.style.position = 'fixed';
            dot2.style.opacity = '1';
            const ARC = 50;         // px, peak height of each word-to-word hop
            let bx; let by;
            const firstWord = bouncePhrase1.children[0];
            const firstRect = firstWord ? firstWord.getBoundingClientRect() : null;
            if (p3 < FALL_FRAC && firstRect && dot2PageY !== null) {
              // Falling from dot2's resting spot (converted from its
              // frozen page-Y to a live viewport-Y, since it's page-
              // anchored right up until this instant) down onto the
              // first word.
              const ft = p3 / FALL_FRAC;
              const restX = window.innerWidth / 2;
              const restY = dot2PageY - y;
              const wordTargetX = firstRect.left + firstRect.width / 2;
              const wordTargetY = firstRect.top;
              bx = restX + (wordTargetX - restX) * ft;
              by = restY + (wordTargetY - restY) * ft;
            } else if (p3 < HOP_END) {
              // Hops FROM each word TO the next one across the combined
              // sequence (phrase 1's words, then phrase 2's) — both X
              // and Y are interpolated between the two words, arcing up
              // via a parabola added on top of that straight line, so
              // the ball actually travels left-to-right during the hop
              // instead of bouncing in place over one word and then
              // teleporting sideways to the next.
              const hopP = (p3 - FALL_FRAC) / (HOP_END - FALL_FRAC);
              // total - 1 hop segments between total words.
              const wp = Math.max(0, Math.min(total - 1.001, hopP * (total - 1)));
              const idx = Math.floor(wp);
              const tw = wp - idx;
              const wordA = wordAt(idx);
              const wordB = wordAt(idx + 1);
              // "change" and "fight" light up in the brand gradient once
              // the ball has reached (or passed) them, and un-light when
              // scrolling back past them — reversible, same as
              // everything else in this sequence, rather than a
              // permanent mark.
              for (let i = 0; i < total; i++) {
                const w = wordAt(i);
                if (w && /^(change|fight)$/i.test(w.textContent.trim())) {
                  w.classList.toggle('landing-bounce-word--hit', wp >= i);
                }
              }
              if (wordA && wordB) {
                const ra = wordA.getBoundingClientRect();
                const rb = wordB.getBoundingClientRect();
                const ax = ra.left + ra.width / 2;
                const ay = ra.top;
                const targetBx = rb.left + rb.width / 2;
                const targetBy = rb.top;
                bx = ax + (targetBx - ax) * tw;
                const baseY = ay + (targetBy - ay) * tw;
                by = baseY - 4 * ARC * tw * (1 - tw);
              }
            } else {
              // Bounces off the last word once more, this time exiting
              // toward the right edge of the screen and fading out —
              // it doesn't travel to where dot3 lands at all. dot3's own
              // entrance (flying in from the left, into the "O") is a
              // separate, deliberately disconnected beat once this one
              // is gone, not a continuation of this ball's path.
              const lastWord = wordAt(total - 1);
              const lastRect = lastWord ? lastWord.getBoundingClientRect() : null;
              if (lastRect) {
                const ft = (p3 - HOP_END) / (1 - HOP_END);
                const ax = lastRect.left + lastRect.width / 2;
                const ay = lastRect.top;
                const exitX = window.innerWidth - 40;
                bx = ax + (exitX - ax) * ft;
                by = ay - 4 * ARC * ft * (1 - ft);
              }
            }
            if (bx !== undefined) {
              dot2.style.left = `${bx}px`;
              dot2.style.top = `${by}px`;
            }
            // Everything here — background, text, and the ball itself —
            // stays fully opaque for the entire bounce-and-exit. It only
            // disappears as a hard cut once the ball is completely gone
            // (p3 >= 1, above), never fading while still on screen.
            const fadeIn = Math.min(1, p3 / 0.2);
            bounce.style.background = `rgba(6, 6, 12, ${fadeIn})`;
            dot2.style.opacity = '1';
          }
        }

        // ---- Transition 4: #philosophy -> dot above the stats block ----
        // Same pin + scale + border-radius mechanism as transitions 1
        // (hero) and 2 (slogan) above, not the clip-path taper an earlier
        // version of this used — that could only ever round the bottom
        // two corners (or, morphing further, looked squared-off doing
        // the top two), and wasn't reliably reversible. Scaling the
        // *whole*, already-square box toward a target point (with
        // border-radius racing scale toward a full circle at the same
        // time) closes all four corners evenly, and — since it's plain
        // per-frame style driven by scroll position, not a CSS
        // transition/timer — scrolling back up unwinds it exactly like
        // hero/slogan already do, for free.
        const H1H2H3 = H1H2 + H3;
        if (y < H1H2H3) {
          philo.style.visibility = 'visible';
          philo.style.position = '';
          philo.style.top = '';
          philo.style.left = '';
          philo.style.transform = '';
          philo.style.transformOrigin = '';
          philo.style.borderRadius = '';
          philoInner.style.opacity = '1';
          dot4.style.opacity = '0';
          document.querySelector('.landing-funnel-stats')?.classList.remove('is-visible');
        } else {
          const H4 = (philoH || window.innerHeight) * PHILO_SCROLL_MULT;
          // First HOLD_FRAC of the range is spent fully visible/readable
          // — pinned, but not yet shrinking — before the remaining
          // (1 - HOLD_FRAC) actually animates the collapse. Without this,
          // the shrink would start reading the very instant the text
          // itself had barely finished fading in.
          const HOLD_FRAC = 0.35;
          const p4raw = Math.max(0, Math.min(1, (y - H1H2H3) / H4));
          const p4 = p4raw < HOLD_FRAC ? 0 : (p4raw - HOLD_FRAC) / (1 - HOLD_FRAC);
          document.querySelector('.landing-funnel-stats')?.classList.toggle('is-visible', p4 >= 1);
          if (p4 >= 1) {
            philo.style.visibility = 'hidden';
            if (dot4PageY === null) dot4PageY = y + targetY;
            dot4.style.top = `${dot4PageY}px`;
            dot4.style.opacity = '1';
          } else {
            dot4.style.opacity = '0';
            philo.style.visibility = 'visible';
            philo.style.position = 'fixed';
            philo.style.top = '0';
            philo.style.left = '0';
            const w4 = window.innerWidth;
            const H4box = philoH || window.innerHeight;
            philo.style.transformOrigin = `50% ${(targetY / H4box) * 100}%`;
            philo.style.transform = `scale(${1 - p4 * (1 - DOT / w4)}, ${1 - p4 * (1 - DOT / H4box)})`;
            const big4 = Math.max(w4, H4box);
            philo.style.borderRadius = `${p4 * big4}px`;
            philoInner.style.opacity = String(Math.max(0, 1 - p4 * 1.6));
          }
        }
      });
    };
    remeasure();
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', remeasure);
    return () => {
      if (raf !== null) cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', remeasure);
    };
  }, []);

  return (
    <div className="landing" id="top">

      <LandingNav lang={lang} toggleLang={toggleLang} home />

      {/* ─── HERO: same video banner as the teacher-facing Home page ── */}
      {/* Reuses Home's own banner-video.mp4 + dark veil + glassy "magic
       * search" plate — same visual language a teacher already knows
       * from inside the app, just not wired to anything yet (design pass
       * only; the plan is to eventually let it generate a sample lesson).
       * The slogan stays in its own dedicated section underneath. */}
      <section className="landing-hero" ref={heroRef}>
        <div className="landing-hero-fade" ref={heroInnerRef}>
          <video
            className="landing-hero-video"
            autoPlay
            muted
            loop
            playsInline
            aria-hidden="true"
          >
            <source src={process.env.PUBLIC_URL + '/banner-video.mp4'} type="video/mp4" />
          </video>
          <div className="landing-hero-veil" />
          <div className="landing-hero-search-wrap">
            <div className="landing-hero-search-glow">
              <SearchRing />
              <div className="landing-hero-search-border">
                <input
                  className="landing-hero-search-input"
                  type="text"
                  placeholder={t.hero.searchPlaceholder}
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  // Delayed so a click on a suggestion (a blur-triggering
                  // action) still registers before the list unmounts.
                  onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
                />
                <span className="landing-hero-search-icon">✨</span>
              </div>
            </div>
            {searchFocused && (
              <div className="landing-hero-search-suggestions">
                <div className="landing-hero-search-suggestions-label">{t.hero.suggestionsLabel}</div>
                {searchSuggestions.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    className="landing-hero-search-suggestion"
                    // onMouseDown, not onClick — fires before the input's
                    // onBlur, so the value actually lands in the field
                    // instead of the dropdown having already closed.
                    onMouseDown={() => setSearchValue(s)}
                  >
                    <span className="landing-hero-search-suggestion-icon">✨</span>
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
      {/* Reserves the flow space .landing-hero would occupy while it's
       * position: fixed for the shrink animation above. */}
      <div className="landing-hero-spacer" ref={heroSpacerRef} aria-hidden />

      {/* ─── SLOGAN ────────────────────────────────────────────── */}
      {/* This section is itself the shrink-to-dot SOURCE for the next
       * transition (slogan -> manifesto) — see the effect above. The
       * dot block 1 lands here is rendered separately, further down,
       * as a page-level sibling, precisely so it isn't a descendant of
       * this section and doesn't get dragged along when this shrinks. */}
      <section className="landing-slogan" ref={slogRef}>
        <div className="landing-slogan-fade" ref={slogInnerRef}>
          <AmbientOrbs variant="slogan" />
          <h1 className="landing-slogan-title">
            <span className="landing-reveal landing-reveal--1">{t.hero.tagline}</span>
            <br />
            <span className="landing-slogan-em landing-reveal landing-reveal--2">{t.hero.taglineEm}</span>
          </h1>
          <p className="landing-slogan-sub landing-reveal landing-reveal--3">{t.hero.sub}</p>
          <div className="landing-reveal landing-reveal--4">
            <button className="landing-slogan-cta" onClick={() => navigate('/register')}>
              {t.hero.cta} <span aria-hidden>→</span>
            </button>
          </div>
        </div>
      </section>
      {/* Reserves the flow space .landing-slogan occupies while it's
       * position: fixed for its own shrink animation. */}
      <div className="landing-slogan-spacer" ref={slogSpacerRef} aria-hidden />

      {/* Landed dots — page-level, not inside any shrinking section; see
       * the effect above for why. Always rendered (default opacity: 0 in
       * CSS); the effect sets their position and toggles opacity per
       * frame directly via ref, same pattern as the sections above. */}
      <span className="landing-chain-dot" ref={dot1Ref} aria-hidden />
      <span className="landing-chain-dot landing-chain-dot--light" ref={dot2Ref} aria-hidden />
      <span className="landing-chain-dot" ref={dot3Ref} aria-hidden />
      <span className="landing-chain-dot" ref={dot4Ref} aria-hidden />

      {/* ─── MANIFESTO ─────────────────────────────────────────── */}
      <div className="landing-manifesto-block">
        <AmbientOrbs variant="manifesto-a" />
        <BouncingManifesto
          phrase1={t.manifesto[0]}
          phrase2={t.manifesto[1]}
          sectionRef={bounceRef}
          phrase1Ref={bouncePhrase1Ref}
          phrase2Ref={bouncePhrase2Ref}
        />
        {/* Reserves .landing-bounce's flow space while it's fixed for its
         * own scroll-driven hop/slide animation. */}
        <div className="landing-bounce-spacer" ref={bounceSpacerRef} aria-hidden />
        {/* Higher threshold than the default 0.3: this block is 104vh
         * tall with its text centered ~halfway down, so 30% visible
         * triggered the reveal well before the text itself had
         * scrolled into view — the 1200ms transition was finishing
         * off-screen, making it look like it just appeared already
         * settled. This section is itself the shrink-to-dot SOURCE for
         * the next transition (philosophy -> stats), same as slogan and
         * bounce above it are for theirs — see transition 4 in the
         * effect above. */}
        <ManifestoLine
          text={t.manifesto[2]}
          large
          id="philosophy"
          manualReveal
          sectionRef={philoRef}
          innerRef={philoInnerRef}
        />
        {/* Reserves .landing-manifesto.is-large's flow space while it's
         * fixed for its own shrink animation. */}
        <div className="landing-philo-spacer" ref={philoSpacerRef} aria-hidden />
      </div>

      {/* ─── STATS STRIP ───────────────────────────────────────── */}
      <StatsStrip stats={t.stats} />

      {/* ─── DIAGRAM (chaos → focus) ───────────────────────────── */}
      <DiagramSection title={t.diagramTitle} />

      {/* ─── PRODUCT SHOWCASE ──────────────────────────────────── */}
      <ShowcaseSection showcase={t.showcase} />

      {/* ─── FEATURES ──────────────────────────────────────────── */}
      <section className="landing-features" id="features">
        <AmbientOrbs variant="features" />
        {t.features.map((f, i) => (
          <FeatureLine key={f.key} feature={f} index={i} />
        ))}
      </section>

      {/* ─── ABOUT US ──────────────────────────────────────────── */}
      <AboutSection about={t.about} />

      {/* ─── CLOSING CTA ───────────────────────────────────────── */}
      <section className="landing-closing">
        <ClosingGlow />
        <h2 className="landing-closing-line">
          {t.closing.line.split('\n').map((line, i, arr) => (
            <React.Fragment key={i}>
              {line.split('|').map((piece, idx) => (
                idx % 2 === 0
                  ? <React.Fragment key={idx}>{piece}</React.Fragment>
                  : <span key={idx} className="landing-closing-em">{piece}</span>
              ))}
              {i < arr.length - 1 && <br />}
            </React.Fragment>
          ))}
        </h2>
        <button className="landing-closing-cta" onClick={() => navigate('/register')}>
          {t.closing.cta} <span aria-hidden>→</span>
        </button>
      </section>

      {/* ─── FOOTER ────────────────────────────────────────────── */}
      <footer className="landing-footer">
        <div className="landing-footer-brand">{t.footer.rights}</div>
        <div className="landing-footer-links">
          <a href="/privacy">{t.footer.privacy}</a>
          <a href="/terms">{t.footer.terms}</a>
          <a href="mailto:novas.edtech@gmail.com">{t.footer.contact}</a>
        </div>
      </footer>
    </div>
  );
}

// ============================================================
// Visual decorations
// ============================================================

// Gradient ring drawn around the hero search plate on hover/focus, as an
// SVG rounded-rect stroke with fill="none" — this has zero paint in the
// middle by construction, unlike a masked background or a full-rect div
// sitting behind the plate (both of which were tried here and bled
// rainbow color into the plate's interior via backdrop-filter, since
// blur/alpha-compositing samples whatever is actually painted behind an
// element regardless of what visually covers it on top). A stroke-only
// shape has nothing there to bleed.
// viewBox is kept in sync with the plate's real measured pixel size via
// ResizeObserver so the rx corner radius isn't stretched into an ellipse
// by non-uniform SVG scaling — the plate is a wide pill, not a square.
function SearchRing() {
  const ref = useRef(null);
  const [box, setBox] = useState({ w: 424, h: 58 });

  useEffect(() => {
    const el = ref.current?.parentElement;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      if (width > 0 && height > 0) setBox({ w: width, h: height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <svg
      ref={ref}
      className="landing-hero-search-ring"
      viewBox={`0 0 ${box.w} ${box.h}`}
      preserveAspectRatio="none"
      aria-hidden
    >
      <defs>
        <linearGradient id="searchRingGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#ff0000" />
          <stop offset="16%" stopColor="#ffff00" />
          <stop offset="33%" stopColor="#00ff00" />
          <stop offset="50%" stopColor="#00ffff" />
          <stop offset="66%" stopColor="#0000ff" />
          <stop offset="83%" stopColor="#ff00ff" />
          <stop offset="100%" stopColor="#ff0000" />
          {/* SMIL rotation of the gradient itself — still supported in
           * Chrome — stands in for Home's animated background-position
           * slide, since a plain SVG paint server has no CSS background
           * to animate. */}
          <animateTransform
            attributeName="gradientTransform"
            type="rotate"
            from="0 0.5 0.5"
            to="360 0.5 0.5"
            dur="3s"
            repeatCount="indefinite"
          />
        </linearGradient>
      </defs>
      <rect
        x="1.5" y="1.5"
        width={Math.max(0, box.w - 3)}
        height={Math.max(0, box.h - 3)}
        rx="16" ry="16"
        fill="none"
        stroke="url(#searchRingGrad)"
        strokeWidth="3"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

function AmbientOrbs({ variant = 'default' }) {
  // Decorative gradient blobs that drift slowly in the background. Pure CSS
  // animation via the CSS class — JS just stamps the markup. Hidden from
  // assistive tech.
  return (
    <div className={`landing-orbs landing-orbs--${variant}`} aria-hidden>
      <span className="landing-orb landing-orb--indigo" />
      <span className="landing-orb landing-orb--violet" />
      <span className="landing-orb landing-orb--fuchsia" />
    </div>
  );
}

// Strips the `\n` line break and `|...|` gradient markers ManifestoLine
// understands and returns a flat word list — this component renders each
// phrase as one single-line row (so word positions are simple, unbroken
// left-to-right coordinates for the ball to target) rather than
// preserving the original two-line layout.
function phraseWords(text) {
  return text.replace(/\n/g, ' ').replace(/\|/g, '').split(' ').filter(Boolean);
}

function BouncingManifesto({ phrase1, phrase2, sectionRef, phrase1Ref, phrase2Ref }) {
  const words1 = phraseWords(phrase1);
  const words2 = phraseWords(phrase2);
  return (
    <section className="landing-bounce" ref={sectionRef}>
      <div className="landing-bounce-phrase" ref={phrase1Ref}>
        {words1.map((w, i) => <span key={i} className="landing-bounce-word">{w}</span>)}
      </div>
      <div className="landing-bounce-phrase landing-bounce-phrase--2" ref={phrase2Ref}>
        {words2.map((w, i) => <span key={i} className="landing-bounce-word">{w}</span>)}
      </div>
    </section>
  );
}

// "new philosophy" hover effect: a full-screen starfield takeover with
// the letters of "New Philosophy" floating across it. The overlay is
// rendered through a portal straight into document.body — several
// ancestors in the manifesto text tree carry a `filter` (this span's
// own drop-shadow, and .landing-manifesto-line's blur for its reveal
// transition, active even at blur(0)), and ANY non-none filter on an
// ancestor turns it into the containing block for position: fixed
// descendants — trapping the overlay inside a few words of text
// instead of covering the viewport. A portal sidesteps the whole
// ancestor chain rather than needing to audit every filter/transform
// between here and the page root.
// Starting spot/size/timing per letter of "NewPhilosophy" (space
// dropped) — deliberately scattered rather than a grid. Position here
// is only the STARTING point now — the physics loop in PhiloHoverEm
// takes over from there, so these read as spawn points, not a fixed
// layout.
const PHILO_LETTERS = [
  { ch: 'N', top: 15, left: 8,  size: 80,  dur: 14.0, delay: -0.5 },
  { ch: 'e', top: 25, left: 80, size: 50,  dur: 12.0, delay: -2 },
  { ch: 'w', top: 62, left: 14, size: 100, dur: 16.0, delay: -4 },
  { ch: 'P', top: 10, left: 52, size: 120, dur: 13.0, delay: -1 },
  { ch: 'h', top: 72, left: 70, size: 60,  dur: 15.0, delay: -3 },
  { ch: 'i', top: 42, left: 40, size: 40,  dur: 10.0, delay: -5 },
  { ch: 'l', top: 82, left: 30, size: 90,  dur: 18.0, delay: -2.5 },
  { ch: 'o', top: 20, left: 25, size: 55,  dur: 12.0, delay: -6 },
  { ch: 's', top: 55, left: 86, size: 70,  dur: 14.0, delay: -1.5 },
  { ch: 'o', top: 35, left: 4,  size: 45,  dur: 17.0, delay: -3.5 },
  { ch: 'p', top: 87, left: 58, size: 65,  dur: 12.0, delay: -4.5 },
  { ch: 'h', top: 5,  left: 78, size: 35,  dur: 14.0, delay: -0.8 },
  { ch: 'y', top: 65, left: 46, size: 75,  dur: 16.0, delay: -5.5 },
];

function PhiloHoverEm({ children }) {
  const [hovered, setHovered] = useState(false);
  const wrapRefs = useRef([]);

  // A small 2D physics loop — separate from the CSS keyframe each
  // letter still runs for its own grow/rotate/shadow "coming at you"
  // effect (that stays exactly as-is, driven by the CSS animation on
  // the INNER span). This loop only drives each letter's WRAPPER
  // position (left/top), bouncing off the screen edges and, pairwise,
  // off each other — real collisions, not independent animations that
  // happen to occupy the same screen.
  useEffect(() => {
    if (!hovered) return undefined;
    // Velocity points AWAY from screen-center, scaled by how far off-
    // center the letter already is — the classic hyperspace/starfield
    // field: things near the middle barely drift while things nearer
    // the edge rocket outward. Paired with each letter's own scale-up
    // (see landingPhiloFloat), that's what actually reads as "we're
    // moving forward" rather than "things are randomly floating".
    const items = PHILO_LETTERS.map((l) => {
      const dx = l.left - 50;
      const dy = l.top - 50;
      const dist = Math.hypot(dx, dy) || 1;
      const speed = 0.015 + (dist / 70) * 0.07;
      return {
        x: l.left,
        y: l.top,
        vx: (dx / dist) * speed,
        vy: (dy / dist) * speed,
        r: l.size / 2,
      };
    });
    let raf = requestAnimationFrame(step);
    function step() {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      for (const it of items) {
        it.x += it.vx;
        it.y += it.vy;
        if (it.x < 0) { it.x = 0; it.vx *= -1; }
        if (it.x > 100) { it.x = 100; it.vx *= -1; }
        if (it.y < 0) { it.y = 0; it.vy *= -1; }
        if (it.y > 100) { it.y = 100; it.vy *= -1; }
      }
      for (let i = 0; i < items.length; i++) {
        for (let j = i + 1; j < items.length; j++) {
          const a = items[i];
          const b = items[j];
          const dx = (a.x - b.x) / 100 * vw;
          const dy = (a.y - b.y) / 100 * vh;
          const dist = Math.hypot(dx, dy) || 0.001;
          // 0.55 fudge factor: r is half the font-size, but the actual
          // glyph is narrower than its own em box, so a full-radius
          // check made letters "bounce" while still visibly apart.
          const minDist = (a.r + b.r) * 0.55;
          if (dist < minDist) {
            const nx = dx / dist;
            const ny = dy / dist;
            const rel = (a.vx - b.vx) * nx + (a.vy - b.vy) * ny;
            if (rel < 0) {
              a.vx -= rel * nx; a.vy -= rel * ny;
              b.vx += rel * nx; b.vy += rel * ny;
            }
            const overlapPx = (minDist - dist) / 2;
            a.x += (nx * overlapPx / vw) * 100;
            a.y += (ny * overlapPx / vh) * 100;
            b.x -= (nx * overlapPx / vw) * 100;
            b.y -= (ny * overlapPx / vh) * 100;
          }
        }
      }
      items.forEach((it, i) => {
        const el = wrapRefs.current[i];
        if (el) {
          el.style.left = `${it.x}%`;
          el.style.top = `${it.y}%`;
        }
      });
      raf = requestAnimationFrame(step);
    }
    return () => cancelAnimationFrame(raf);
  }, [hovered]);

  return (
    <span
      className="landing-manifesto-em landing-philo-hover-trigger"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
      {createPortal(
        <div className={`landing-philo-hover-overlay${hovered ? ' is-visible' : ''}`} aria-hidden>
          {PHILO_LETTERS.map((l, i) => (
            <span
              key={i}
              ref={(el) => { wrapRefs.current[i] = el; }}
              className="landing-philo-hover-letter-wrap"
              style={{ top: `${l.top}%`, left: `${l.left}%` }}
            >
              <span
                className="landing-philo-hover-letter"
                style={{ fontSize: `${l.size}px`, animationDuration: `${l.dur}s`, animationDelay: `${l.delay}s` }}
              >
                {l.ch}
              </span>
            </span>
          ))}
        </div>,
        document.body,
      )}
    </span>
  );
}

function ManifestoLine({
  text, large = false, id, viewThreshold = 0.3, repeat = false, edgeCheck = false, manualReveal = false,
  sectionRef, innerRef,
}) {
  // manualReveal: skips this component's own IntersectionObserver-based
  // guess entirely — the Landing component's main scroll effect toggles
  // .is-visible on this section directly instead, at the exact same
  // moment (p3 >= 1) the previous block's hard-cut white reveal happens,
  // which no ratio/edge heuristic here could line up with as precisely.
  const [autoRef, inViewAuto] = useInView(viewThreshold, !repeat, edgeCheck);
  const inView = manualReveal ? false : inViewAuto;
  // large passes its own sectionRef/innerRef down from Landing — its
  // pin/scale/border-radius shrink-to-dot transition (transition 4,
  // same mechanism as hero/slogan) needs direct refs, same as those two,
  // rather than the plain IntersectionObserver ref every other manifesto
  // line uses.
  const ref = sectionRef || autoRef;
  const lines = text.split('\n');
  // Two layered marker systems:
  //   |x|    → x renders in the brand gradient (.landing-manifesto-em)
  //   <<x>>  → x renders as the wordmark — NextSphere font + a small
  //            decorative dot inside any letter "O" (.landing-manifesto-brand)
  const renderBrand = (text) => Array.from(text).map((ch, i) => (
    ch === 'O'
      ? <span key={i} className="landing-brand-o">{ch}</span>
      : <React.Fragment key={i}>{ch}</React.Fragment>
  ));
  const renderLineParts = (line) => {
    const gradParts = line.split('|');
    return gradParts.flatMap((piece, gi) => {
      if (gi % 2 === 1) {
        return [<PhiloHoverEm key={`g${gi}`}>{piece}</PhiloHoverEm>];
      }
      // Plain piece — scan for <<...>> brand markers
      const brandParts = piece.split(/<<([^>]+)>>/);
      return brandParts.map((bp, bi) => (
        bi % 2 === 1
          ? <span key={`b${gi}-${bi}`} className="landing-manifesto-brand">{renderBrand(bp)}</span>
          : <React.Fragment key={`p${gi}-${bi}`}>{bp}</React.Fragment>
      ));
    });
  };
  const heading = (
    <h2>
      {lines.map((line, i) => (
        <span
          key={i}
          className={`landing-manifesto-line landing-manifesto-line--${i + 1}`}
        >
          {renderLineParts(line)}
        </span>
      ))}
    </h2>
  );
  return (
    <section
      ref={ref}
      id={id}
      className={`landing-manifesto${inView ? ' is-visible' : ''}${large ? ' is-large' : ''}`}
    >
      {large ? <div className="landing-manifesto-fade" ref={innerRef}>{heading}</div> : heading}
    </section>
  );
}

// .is-visible is toggled directly on this section by the Landing
// component's main scroll effect (transition 4, off p4 >= 1 — the exact
// instant #philosophy finishes landing as a dot), the same way every
// other pinned/shrinking section's target reveal works — not by this
// component's own scroll position. inView here just mirrors that class
// via a MutationObserver (React state, so the count-up in Stat below has
// a real boolean to key off) rather than duplicating the scroll math.
function StatsStrip({ stats }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const sync = () => setInView(el.classList.contains('is-visible'));
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(el, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  return (
    <section ref={ref} className={`landing-funnel-stats${inView ? ' is-visible' : ''}`}>
      <div className="landing-funnel-stats-row">
        {stats.map((s, i) => (
          <Stat key={i} value={s.value} label={s.label} inView={inView} index={i} />
        ))}
      </div>
    </section>
  );
}

function Stat({ value, label, inView, index }) {
  const animated = useCountUp(value, inView, 900 + index * 150);
  const match = String(value).match(/^\d+(.*)$/);
  const suffix = match ? match[1] : '';
  return (
    <div className="landing-stat" style={{ transitionDelay: `${index * 90}ms` }}>
      <div className="landing-stat-value">
        {typeof animated === 'number' ? `${animated}${suffix}` : value}
      </div>
      <div className="landing-stat-label">{label}</div>
    </div>
  );
}

function DiagramSection({ title }) {
  const [ref, inView] = useInView(0.25);
  const lines = title.split('\n');

  // Hub at the centre of the viewBox; nodes orbit it on a single ring.
  const HUB_X = 550;
  const HUB_Y = 350;
  const ORBIT_R = 280;
  const NODES_RAW = [
    { label: 'Zoom',           w:  70 },
    { label: 'Google Meets',   w: 140 },
    { label: 'Miro',           w:  70 },
    { label: 'Telegram',       w: 110 },
    { label: 'Screen Sharing', w: 150 },
    { label: 'Quizlet',        w: 100 },
    { label: 'ChatGPT',        w: 100 },
    { label: 'Canva',          w:  80 },
  ];
  const nodes = NODES_RAW.map((n, i) => {
    // Start at -90° (12 o'clock) and walk clockwise so the first pill
    // sits above the hub and the eight nodes are evenly spaced.
    const angle = (-90 + i * (360 / NODES_RAW.length)) * Math.PI / 180;
    return {
      ...n,
      x: HUB_X + ORBIT_R * Math.cos(angle),
      y: HUB_Y + ORBIT_R * Math.sin(angle),
    };
  });

  return (
    <section ref={ref} className={`landing-diagram${inView ? ' is-visible' : ''}`}>
      <AmbientOrbs variant="diagram" />
      <h3 className="landing-diagram-title">
        {lines.map((line, i) => (
          <React.Fragment key={i}>
            {line.split('|').map((piece, idx) =>
              idx % 2 === 0
                ? <React.Fragment key={idx}>{piece}</React.Fragment>
                : <span key={idx} className="landing-manifesto-em">{piece}</span>
            )}
            {i < lines.length - 1 && <br />}
          </React.Fragment>
        ))}
      </h3>
      <div className="landing-diagram-stage">
        <svg
          className="landing-diagram-svg"
          viewBox="0 0 1100 700"
          preserveAspectRatio="xMidYMid meet"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden
        >
          <defs>
            <linearGradient id="dgrad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#818cf8" stopOpacity="0.95" />
              <stop offset="50%" stopColor="#a78bfa" stopOpacity="0.95" />
              <stop offset="100%" stopColor="#f472b6" stopOpacity="0.95" />
            </linearGradient>
            <radialGradient id="dhub" cx="0.5" cy="0.5" r="0.5">
              <stop offset="0%" stopColor="#a78bfa" stopOpacity="1" />
              <stop offset="100%" stopColor="#6366f1" stopOpacity="0.7" />
            </radialGradient>
          </defs>

          {/* Faint dashed ring at the orbital radius — gives the eye a
           * track to follow the motion. */}
          <circle
            className="landing-diagram-orbit-ring"
            cx={HUB_X} cy={HUB_Y} r={ORBIT_R}
            fill="none"
            stroke="url(#dgrad)"
            strokeWidth="1"
            strokeDasharray="2 9"
          />

          {/* Orbiting tool nodes.
           *
           * Layout strategy:
           *   1. Outer SVG `<g transform="translate(HUB_X, HUB_Y)">` moves
           *      the local origin to the hub centre. Every CSS transform
           *      below defaults to that origin (no transform-origin
           *      needed, no cross-browser bbox quirks).
           *   2. `.emerge` is OUTSIDE `.orbit`. Its `scale(0)` collapses
           *      everything inside — including the orbit's translate —
           *      to (0, 0) world = hub, so the pill literally grows out
           *      from under the NOVAS disc.
           *   3. `.orbit` keyframes are
           *        rotate(θ)  translate(R, 0)  rotate(-θ)
           *      Composed in CSS this is the classic "orbit with upright
           *      content" pattern — the first rotation places the local
           *      x-axis at angle θ, the translate pushes the node out by
           *      R along that axis, and the second rotation unwinds the
           *      content so the text never goes upside-down.
           *   4. Per-node animation-delay starts each pill at a different
           *      angle so the eight of them are evenly spaced. */}
          {nodes.map((n, i) => (
            <g key={i} transform={`translate(${HUB_X}, ${HUB_Y})`}>
              <g className={`landing-diagram-emerge landing-diagram-emerge--${i}`}>
                <g className={`landing-diagram-orbit landing-diagram-orbit--${i}`}>
                  <g className={`landing-diagram-node landing-diagram-node--${i}`}>
                    <g className="landing-diagram-node-float">
                      <rect
                        x={-n.w / 2}
                        y={-22}
                        width={n.w}
                        height={44}
                        rx={22}
                        fill="rgba(20, 20, 32, 0.92)"
                        stroke="rgba(167, 139, 250, 0.55)"
                        strokeWidth="1.4"
                      />
                      <text
                        textAnchor="middle"
                        y="5"
                        fontSize="16"
                        fontWeight="500"
                        fill="#e2e8f0"
                        fontFamily="Inter, -apple-system, sans-serif"
                      >
                        {n.label}
                      </text>
                    </g>
                  </g>
                </g>
              </g>
            </g>
          ))}

          {/* Central hub — drawn LAST so it sits on top of any node that
           * happens to be at the bottom of its emerge animation (still at
           * the centre). That's how we get the "comes out from under the
           * NOVAS circle" effect. */}
          <g transform="translate(550, 350)">
            <g className="landing-diagram-hub">
              <circle r="100" fill="url(#dhub)" opacity="0.12" className="landing-diagram-hub-ring landing-diagram-hub-ring--outer" />
              <circle r="72"  fill="url(#dhub)" opacity="0.28" className="landing-diagram-hub-ring landing-diagram-hub-ring--mid" />
              <circle r="52"  fill="url(#dhub)" />
            </g>
          </g>
        </svg>
      </div>
    </section>
  );
}

function FeatureLine({ feature, index }) {
  const [ref, inView] = useInView(0.2);
  const flipped = index % 2 === 1;
  return (
    <article
      ref={ref}
      className={`landing-feature${inView ? ' is-visible' : ''}${flipped ? ' is-flipped' : ''}`}
    >
      <div className="landing-feature-key-wrap">
        <h3 className="landing-feature-key">{feature.key}</h3>
        <FeatureGlyph index={index} />
      </div>
      <div className="landing-feature-content">
        <h4 className="landing-feature-title">{feature.title}</h4>
        <p className="landing-feature-text">{feature.text}</p>
      </div>
    </article>
  );
}

// Abstract decoration that lives next to each feature key — a different
// shape per feature so the page has visual variety beyond text. No real
// product screenshots yet; these are placeholders that already feel like
// "something is here".
function FeatureGlyph({ index }) {
  const variants = [
    // 0 LESSONS — stacked horizontal lines (a generated outline)
    (
      <svg viewBox="0 0 200 200" aria-hidden>
        <g className="lg-pulse" stroke="url(#g0)" strokeWidth="3" strokeLinecap="round" fill="none">
          <line x1="30" y1="50" x2="170" y2="50" />
          <line x1="30" y1="85" x2="140" y2="85" />
          <line x1="30" y1="120" x2="160" y2="120" />
          <line x1="30" y1="155" x2="100" y2="155" />
        </g>
        <defs>
          <linearGradient id="g0"><stop offset="0%" stopColor="#818cf8"/><stop offset="100%" stopColor="#f472b6"/></linearGradient>
        </defs>
      </svg>
    ),
    // 1 EXERCISES — grid of cells
    (
      <svg viewBox="0 0 200 200" aria-hidden>
        <g stroke="url(#g1)" strokeWidth="2" fill="rgba(167,139,250,0.06)">
          {[0,1,2].map(r => [0,1,2].map(c => (
            <rect key={`${r}${c}`} x={30 + c*50} y={30 + r*50} width="40" height="40" rx="6" />
          )))}
        </g>
        <defs>
          <linearGradient id="g1"><stop offset="0%" stopColor="#a78bfa"/><stop offset="100%" stopColor="#f472b6"/></linearGradient>
        </defs>
      </svg>
    ),
    // 2 AUTHENTIC — overlapping rounded rectangles (article cards)
    (
      <svg viewBox="0 0 200 200" aria-hidden>
        <g fill="rgba(255,255,255,0.04)" stroke="url(#g2)" strokeWidth="2">
          <rect x="30" y="40" width="120" height="80" rx="10" />
          <rect x="55" y="70" width="120" height="80" rx="10" />
        </g>
        <defs>
          <linearGradient id="g2"><stop offset="0%" stopColor="#818cf8"/><stop offset="100%" stopColor="#a78bfa"/></linearGradient>
        </defs>
      </svg>
    ),
    // 3 HOMEWORK — checklist with one done, one pending
    (
      <svg viewBox="0 0 200 200" aria-hidden>
        <g stroke="url(#g3)" strokeWidth="2.5" fill="none" strokeLinecap="round">
          <circle cx="50" cy="60" r="10" fill="rgba(167,139,250,0.4)" stroke="none" />
          <path d="M44 60 L48 64 L56 56" stroke="#fff" strokeWidth="2.5" />
          <line x1="70" y1="60" x2="170" y2="60" />
          <circle cx="50" cy="100" r="10" />
          <line x1="70" y1="100" x2="150" y2="100" />
          <circle cx="50" cy="140" r="10" />
          <line x1="70" y1="140" x2="160" y2="140" />
        </g>
        <defs>
          <linearGradient id="g3"><stop offset="0%" stopColor="#10b981"/><stop offset="100%" stopColor="#a78bfa"/></linearGradient>
        </defs>
      </svg>
    ),
    // 4 VIDEO CALLS — monitor frame with a play triangle inside
    (
      <svg viewBox="0 0 200 200" aria-hidden>
        <g stroke="url(#g4v)" strokeWidth="2.2" fill="rgba(167,139,250,0.06)" strokeLinejoin="round" strokeLinecap="round">
          <rect x="30" y="50" width="140" height="92" rx="12" />
          <line x1="76" y1="160" x2="124" y2="160" />
          <path d="M88,80 L88,112 L120,96 Z" fill="url(#g4v)" stroke="none" />
        </g>
        <defs>
          <linearGradient id="g4v"><stop offset="0%" stopColor="#818cf8"/><stop offset="100%" stopColor="#f472b6"/></linearGradient>
        </defs>
      </svg>
    ),
    // 5 CHAT — chat bubbles
    (
      <svg viewBox="0 0 200 200" aria-hidden>
        <g stroke="url(#g5)" strokeWidth="2" fill="rgba(244,114,182,0.08)">
          <path d="M30,50 h90 q12,0 12,12 v34 q0,12 -12,12 h-60 l-18,18 v-18 h-12 q-12,0 -12,-12 v-34 q0,-12 12,-12 z" />
          <path d="M82,114 h78 q12,0 12,12 v28 q0,12 -12,12 h-44 l-14,14 v-14 h-20 q-12,0 -12,-12 v-28 q0,-12 12,-12 z" />
        </g>
        <defs>
          <linearGradient id="g5"><stop offset="0%" stopColor="#a78bfa"/><stop offset="100%" stopColor="#f472b6"/></linearGradient>
        </defs>
      </svg>
    ),
    // 6 BUSINESS — single circle with orbits (everything in one place)
    (
      <svg viewBox="0 0 200 200" aria-hidden>
        <g stroke="url(#g6)" fill="none">
          <ellipse cx="100" cy="100" rx="80" ry="32" strokeWidth="1.4" opacity="0.5" />
          <ellipse cx="100" cy="100" rx="80" ry="32" strokeWidth="1.4" opacity="0.5" transform="rotate(60 100 100)" />
          <ellipse cx="100" cy="100" rx="80" ry="32" strokeWidth="1.4" opacity="0.5" transform="rotate(-60 100 100)" />
          <circle cx="100" cy="100" r="22" fill="url(#g6)" />
        </g>
        <defs>
          <linearGradient id="g6"><stop offset="0%" stopColor="#818cf8"/><stop offset="50%" stopColor="#a78bfa"/><stop offset="100%" stopColor="#f472b6"/></linearGradient>
        </defs>
      </svg>
    ),
  ];
  return <div className="landing-feature-glyph">{variants[index % variants.length]}</div>;
}

function ClosingGlow() {
  return (
    <div className="landing-closing-glow" aria-hidden>
      <span />
      <span />
      <span />
    </div>
  );
}

// ─── PRODUCT SHOWCASE ─────────────────────────────────────────
// Browser-frame mockups demonstrating the product. Each card is a
// styled <figure> with a faux Chrome top bar; the body is either a
// real screenshot (set `image` on the COPY shot object) or a
// stylized placeholder so the section ships even before we have
// the final captures.
// Hover state lives per-card so each can compute its own offset to the
// viewport center. On mouseenter we measure the card's bounding box and
// stash dx/dy as CSS variables; the hover transform in the stylesheet
// then translates the card by that delta and scales it up — producing
// a "pop to centre of the screen" feel that's noticeably stronger than
// the previous translateY + 1.06 nudge.
// The fanned frame and its caption used to be one rotated/scaled
// <figure> — tilting the whole thing tilted the caption text too,
// which at 3 captions sitting close together read as overlapping,
// jumbled text. Split into two separate rows now (see ShowcaseSection):
// this renders just the tilted video frame, ShowcaseCaption renders the
// caption on its own, in a plain untransformed row below the fan.
function ShowcaseCard({ shot, index }) {
  return (
    <div
      className={`landing-showcase-card landing-showcase-card--${shot.key}`}
      style={{ transitionDelay: `${index * 140}ms` }}
    >
      <div className="landing-browser-frame">
        <div className="landing-browser-chrome">
          <span className="landing-browser-dot landing-browser-dot--red" />
          <span className="landing-browser-dot landing-browser-dot--amber" />
          <span className="landing-browser-dot landing-browser-dot--green" />
          <div className="landing-browser-url">{shot.url}</div>
        </div>
        <div className="landing-browser-body">
          {shot.video ? (
            <ShowcaseVideo src={shot.video} label={shot.label} />
          ) : shot.image ? (
            <img src={shot.image} alt={shot.label} className="landing-browser-img" />
          ) : (
            <ShowcasePlaceholder kind={shot.key} />
          )}
        </div>
      </div>
    </div>
  );
}

function ShowcaseCaption({ shot, index }) {
  return (
    <div className="landing-showcase-caption" style={{ transitionDelay: `${index * 140}ms` }}>
      <div className="landing-showcase-label">{shot.label}</div>
      <p className="landing-showcase-text">{shot.caption}</p>
    </div>
  );
}

function ShowcaseSection({ showcase }) {
  const [ref, inView] = useInView(0.18);
  const lines = showcase.title.split('\n');

  return (
    <section ref={ref} className={`landing-showcase${inView ? ' is-visible' : ''}`} id="showcase">
      <AmbientOrbs variant="showcase" />

      <header className="landing-showcase-header">
        <div className="landing-showcase-eyebrow">{showcase.eyebrow}</div>
        <h2 className="landing-showcase-title">
          {lines.map((line, i, arr) => (
            <React.Fragment key={i}>
              {line.split('|').map((piece, idx) =>
                idx % 2 === 0
                  ? <React.Fragment key={idx}>{piece}</React.Fragment>
                  : <span key={idx} className="landing-manifesto-em">{piece}</span>
              )}
              {i < arr.length - 1 && <br />}
            </React.Fragment>
          ))}
        </h2>
        <p className="landing-showcase-sub">{showcase.sub}</p>
      </header>

      <div className="landing-showcase-grid">
        {showcase.shots.map((shot, i) => (
          <ShowcaseCard key={shot.key} shot={shot} index={i} />
        ))}
      </div>
      <div className="landing-showcase-captions">
        {showcase.shots.map((shot, i) => (
          <ShowcaseCaption key={shot.key} shot={shot} index={i} />
        ))}
      </div>
    </section>
  );
}

// Auto-playing muted loop, paused when out of view so we don't burn
// the visitor's CPU/bandwidth on three videos at once when only one
// is on screen. Hides itself gracefully (transparent) if the file
// fails to load — the surrounding browser-frame placeholder shows
// through, instead of a broken <video> control. Honours
// prefers-reduced-motion by leaving the poster frame still.
function ShowcaseVideo({ src, label }) {
  const videoRef = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (typeof window !== 'undefined'
        && window.matchMedia
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return; // honour the reduced-motion preference — don't auto-play
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.play?.().catch(() => {});
        } else {
          el.pause?.();
        }
      },
      { threshold: 0.25 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [src]);

  if (failed) return null;
  return (
    <video
      ref={videoRef}
      className="landing-browser-video"
      src={src}
      muted
      loop
      playsInline
      preload="metadata"
      aria-label={label}
      onError={() => setFailed(true)}
    />
  );
}

// Stylized "wireframe with brand tint" placeholders per shot kind.
// Pure SVG/divs so they ship without an image asset; real screenshots
// land via `image` on the shot and replace this entirely.
function ShowcasePlaceholder({ kind }) {
  if (kind === 'editor') {
    return (
      <div className="landing-placeholder landing-placeholder--editor" aria-hidden>
        <div className="ph-row ph-row--title" />
        <div className="ph-paragraph">
          <span /><span /><span style={{ width: '72%' }} />
        </div>
        <div className="ph-paragraph">
          <span /><span style={{ width: '88%' }} /><span style={{ width: '56%' }} />
        </div>
        <div className="ph-card ph-card--accent">
          <div className="ph-card-label">EXERCISE</div>
          <div className="ph-pill-row">
            <span className="ph-pill">apple</span>
            <span className="ph-pill ph-pill--match">яблоко</span>
            <span className="ph-pill">river</span>
            <span className="ph-pill ph-pill--match">река</span>
          </div>
        </div>
      </div>
    );
  }
  if (kind === 'exercise') {
    return (
      <div className="landing-placeholder landing-placeholder--exercise" aria-hidden>
        <div className="ph-row ph-row--small" />
        <div className="ph-grid">
          <div className="ph-tile">light</div>
          <div className="ph-tile ph-tile--match">свет</div>
          <div className="ph-tile">moon</div>
          <div className="ph-tile ph-tile--match">луна</div>
          <div className="ph-tile">river</div>
          <div className="ph-tile ph-tile--correct">река</div>
        </div>
        <div className="ph-status">2 of 6 matched · 1 perfect streak</div>
      </div>
    );
  }
  // student
  return (
    <div className="landing-placeholder landing-placeholder--student" aria-hidden>
      <div className="ph-stat-row">
        <div className="ph-stat"><strong>27</strong><span>lessons</span></div>
        <div className="ph-stat"><strong>184</strong><span>words</span></div>
        <div className="ph-stat ph-stat--accent"><strong>$120</strong><span>balance</span></div>
      </div>
      <div className="ph-list">
        <div className="ph-list-item">
          <span className="ph-list-bullet" />
          <span>Lesson 12 · Past Continuous</span>
          <em>Tue</em>
        </div>
        <div className="ph-list-item ph-list-item--new">
          <span className="ph-list-bullet" />
          <span>Homework · 8 new words</span>
          <em>due Fri</em>
        </div>
        <div className="ph-list-item">
          <span className="ph-list-bullet" />
          <span>Dictionary · review 14 cards</span>
          <em>now</em>
        </div>
      </div>
    </div>
  );
}

// ─── ABOUT US ──────────────────────────────────────────────────
// Story + founder cards. Avatars are placeholder gradient discs
// with the founder's initials — Samvel will drop in real photos
// later (just swap the `photo` URL on each founder in COPY).
function AboutSection({ about }) {
  const [ref, inView] = useInView(0.2);
  const initialsFor = (name) =>
    name.split(/\s+/).filter(Boolean).map(part => part[0]).slice(0, 2).join('').toUpperCase();

  return (
    <section ref={ref} className={`landing-about${inView ? ' is-visible' : ''}`} id="about">
      <AmbientOrbs variant="about" />

      <div className="landing-about-intro">
        <div className="landing-about-eyebrow">{about.eyebrow}</div>
        <h2 className="landing-about-title">{about.title}</h2>
        <p className="landing-about-lead">{about.intro}</p>
        <p className="landing-about-story">{about.story}</p>
      </div>

      <div className="landing-about-founders-block">
        <div className="landing-about-founders-heading">{about.foundersHeading}</div>
        <div className="landing-about-founders">
          {about.founders.map((f, i) => (
            <article key={f.name} className="landing-founder" style={{ transitionDelay: `${i * 120}ms` }}>
              <div className="landing-founder-avatar">
                {f.photo ? (
                  <img src={f.photo} alt={f.name} />
                ) : (
                  <span className="landing-founder-initials" aria-hidden>
                    {initialsFor(f.name)}
                  </span>
                )}
              </div>
              <div className="landing-founder-meta">
                <div className="landing-founder-name">{f.name}</div>
                <div className="landing-founder-role">{f.role}</div>
                <p className="landing-founder-bio">{f.bio}</p>
                {f.linkedin && (
                  <a
                    className="landing-founder-linkedin"
                    href={f.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    LinkedIn ↗
                  </a>
                )}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

